import {type RouteLocationStringParam, type RouteRecordRaw} from 'vue-router';
import {toInt} from '../library/math';
import {addCalendarRoutes} from './calendar';

const ServicesAssistant      = () => import('../views/services/services/assistant/ServicesAssistant.vue');
const ServicesAssistantIntro = () => import('../views/services/services/assistant/ServicesAssistantIntro.vue');
const ServicesAssistantOutro = () => import('../views/services/services/assistant/ServicesAssistantOutro.vue');

const ServiceList   = () => import('../views/services/services/ServiceList.vue');
const ServicesEdit  = () => import('../views/services/services/ServiceEdit.vue');
const ServiceView   = () => import('../views/services/services/ServiceView.vue');
const ServicesStart = () => import('../views/services/services/ServicesStart.vue');

const ServicesEntryUnavailability             = () => import('../views/services/calendar/entry/ServicesEntryUnavailability.vue');
const ServicesCalendarUnavailabilityEntryView = () => import('../views/services/calendar/entry/ServicesCalendarUnavailabilityEntryView.vue');

const ServicesCalendarEdit      = () => import('../views/services/calendar/ServicesCalendarEdit.vue');
const ServicesCalendarList      = () => import('../views/services/calendar/list/ServicesCalendarList.vue');
const ServicesCalendarEntryEdit = () => import('../views/services/calendar/entry/ServicesCalendarEntryEdit.vue');
const ServicesCalendarEntryView = () => import('../views/services/calendar/entry/ServicesCalendarEntryView.vue');
const ServicesCalendarDashboard = () => import('../views/services/calendar/list/dashboard/ServicesCalendarDashboard.vue');

const Settings                 = () => import('../views/services/settings/ServiceSettings.vue');
const SettingsPrivacy          = () => import('../views/services/settings/types/ServicesSettingsPrivacy.vue');
const SettingsLegalNotice      = () => import('../views/services/settings/types/ServicesSettingsLegalNotice.vue');
const ServicesSettingsContactForm      = () => import('../views/services/settings/types/ServicesSettingsContactForm.vue');
const SettingsBooking          = () => import('../views/services/settings/types/ServicesSettingsBooking.vue');
const SettingsContact          = () => import('../views/services/settings/types/ServicesSettingsContact.vue');
const SettingsSite             = () => import('../views/services/settings/types/ServicesSettingsSite.vue');
const SettingsEmail            = () => import('../views/services/settings/types/ServicesSettingsEmail.vue');
const SettingsReminder         = () => import('../views/services/settings/types/ServicesSettingsReminder.vue');
const SettingsCategory         = () => import('../views/services/settings/types/ServicesSettingsCategory.vue');
const SettingsInterface        = () => import('../views/services/settings/types/ServicesSettingsInterface.vue');
const SettingsRights           = () => import('../views/services/settings/types/ServicesSettingsRights.vue');
const SettingsEmailTemplate    = () => import('../views/services/settings/mailTemplates/ServicesSettingsEmailTemplate.vue');
const ServicesMailTemplateEdit = () => import('../views/services/settings/mailTemplates/ServicesMailTemplateEdit.vue');

const Booking            = () => import('../views/services/booking/ServiceBooking.vue');
const BookingService     = () => import('../views/services/booking/steps/ServiceBookingService.vue');
const BookingAppointment = () => import('../views/services/booking/steps/ServiceBookingAppointment.vue');
const BookingClient      = () => import('../views/services/booking/steps/ServiceBookingClient.vue');
const BookingConfirm     = () => import('../views/services/booking/steps/ServiceBookingConfirm.vue');
const BookingCancel      = () => import('../views/services/booking/steps/ServiceBookingCancel.vue');

const resource       = 'services';

const meta: MetaData = {
    module: resource,
    assistant: {
        key: 'services',
        redirect: {name: 'services-start'},
        onComplete: {name: 'services'},
        steps: [
            {
                component: ServicesAssistantIntro,
            },
            {
                title: 'Terminbuchung',
                titleIcon: 'support',
                component: ServicesAssistant,
                componentProps: {resource},
            },
            {
                component: ServicesAssistantOutro,
            },
        ],
    },
};

const metaBooking: MetaData = {
    module: resource,
    layout: 'blank',
    allowLoggedOut: true,
    disableNotifications: true,
    disableScrollOnParamChange: true,
};

let services: RouteRecordRaw[] = [
    //services
    {
        path: '/services/start',
        name: 'services-start',
        component: ServicesStart,
        meta,
    },
    {
        path: '/services',
        name: 'services',
        redirect: {
            name: 'services-calendar-dashboard',
        },
        meta,
    },
    {
        path: '/services/list',
        name: 'service-list',
        component: ServiceList,
        props: () =>
        {
            return {resource};
        },
        meta,
    },
    {
        path: '/services/calendar/dashboard/:id(\\d+)?/:date?',
        name: 'services-calendar-dashboard',
        component: ServicesCalendarDashboard,
        props: (route: RouteLocationStringParam) =>
        {
            return {
                resource,
                id: toInt(route.params.id),
                date: route.params.date,
            };
        },
        meta,
    },
    {
        path: '/services/view/:id(\\d+)',
        name: 'service-view',
        component: ServiceView,
        props: (route: RouteLocationStringParam) =>
        {
            return {
                id: parseInt(route.params.id),
            };
        },
        meta,
    },
    {
        path: '/services/add',
        name: 'service-add',
        component: ServicesEdit,
        props: () =>
        {
            return {
                resource,
                id: 0,
            };
        },
        meta,
    },
    {
        path: '/services/edit/:id/:wildcard*',
        name: 'service-edit',
        component: ServicesEdit,
        props: (route: RouteLocationStringParam) =>
        {
            return {
                resource,
                id: parseInt(route.params.id),
            };
        },
        meta,
    },
    {
        path: '/services/copy/:copyId/:wildcard*',
        name: 'service-copy',
        component: ServicesEdit,
        props: (route: RouteLocationStringParam) =>
        {
            return {
                resource,
                copyId: parseInt(route.params.copyId),
            };
        },
        meta,
    },
    {
        path: '/services/entry/unavailability/:id/:start?',
        name: 'services-entry-unavailability',
        component: ServicesEntryUnavailability,
        props: (route: RouteLocationStringParam) =>
        {
            return {
                resource,
                id: parseInt(route.params.id),
                start: route.params.start || '',
                calendar: route.params.calendar,
            };
        },
        meta,
    },
    {
        path: '/services/entry/unavailability-view/:id/:start?',
        name: 'services-entry-unavailability-view',
        component: ServicesCalendarUnavailabilityEntryView,
        props: (route: RouteLocationStringParam) =>
        {
            return {
                resource,
                id: parseInt(route.params.id),
                start: route.params.start || '',
            };
        },
        meta,
    },
    //settings
    {
        path: '/services/settings/:route(intranet|)?',
        name: 'services-settings',
        component: Settings,
        meta,
        redirect: {name: 'services-settings-site'},
        props()
        {
            return {
                resource,
            };
        },
        children: [
            {
                path: 'site',
                name: 'services-settings-site',
                component: SettingsSite,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'privacy',
                name: 'services-settings-privacy',
                component: SettingsPrivacy,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'legal-notice',
                name: 'services-settings-legal-notice',
                component: SettingsLegalNotice,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'email',
                name: 'services-settings-email',
                component: SettingsEmail,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'email-template',
                name: 'services-settings-email-template',
                component: SettingsEmailTemplate,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'booking',
                name: 'services-settings-booking',
                component: SettingsBooking,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'contact',
                name: 'services-settings-contact',
                component: SettingsContact,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'contact-form',
                name: 'services-settings-contact-form',
                component: ServicesSettingsContactForm,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'reminder',
                name: 'services-settings-reminder',
                component: SettingsReminder,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'categories',
                name: 'services-settings-category',
                component: SettingsCategory,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'interface',
                name: 'services-settings-interface',
                component: SettingsInterface,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'rights',
                name: 'services-settings-rights',
                component: SettingsRights,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },

        ],
    },
    {
        name: 'services-mail-template-edit',
        path: '/services/mail-template/edit/:type/:id',
        component: ServicesMailTemplateEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                type: parseInt(route.params.type),
                id: parseInt(route.params.id),
            };
        },
    },
    // booking
    {
        name: 'services-booking',
        path: '/booking',
        redirect: {name: 'services-booking-service'},
        component: Booking,
        meta: metaBooking,
        children: [
            {
                name: 'services-booking-service',
                path: 'service/:service([\\d-]+)?/:calendar(\\d+)?',
                component: BookingService,
                meta: metaBooking,
            },
            {
                name: 'services-booking-appointment',
                path: 'appointment/:service([\\d-]+)/:calendar(\\d+)?/:date(\\d{4}-\\d{2}-\\d{2})?',
                component: BookingAppointment,
                meta: metaBooking,
            },
            {
                name: 'services-booking-client',
                path: 'client/:service([\\d-]+)/:calendar(\\d+)/:token(\\w{32})',
                component: BookingClient,
                meta: metaBooking,
                props(route: RouteLocationStringParam)
                {
                    return {token: route.params.token};
                },
            },
            {
                name: 'services-booking-confirm',
                path: 'confirm/:token(\\w{32}|\\w{40})',
                component: BookingConfirm,
                meta: metaBooking,
                props(route: RouteLocationStringParam)
                {
                    return {
                        token: route.params.token,
                    };
                },
            },
            {
                name: 'services-booking-cancel',
                path: 'cancel/:token(\\w{32}|\\w{40})',
                component: BookingCancel,
                meta: metaBooking,
                props(route: RouteLocationStringParam)
                {
                    return {
                        token: route.params.token,
                    };
                },
            },
        ],
    },
];

addCalendarRoutes(
    resource,
    services,
    {
        calendarEdit: ServicesCalendarEdit,
        calendarList: ServicesCalendarList,
        entryEdit: ServicesCalendarEntryEdit,
        entryView: ServicesCalendarEntryView,
    },
    meta,
);

export default services;
